/**
 * XRLayerSystem — ECS system for managing XR compositor layers.
 *
 * Phase: Render, Priority: -90 (after XRCameraOverrideSystem at -100)
 * Updates render targets for quad layers and manages layer ordering.
 */

import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import type { XRSessionState } from '../types.js';
import type { XRLayerManager } from './XRLayerManager.js';
// EngineRenderer kept in constructor signature for API compatibility
import type { EngineRenderer } from '../../render/Renderer.js';

export class XRLayerSystem implements ISystem {
  readonly name = 'XRLayerSystem';
  readonly phase = LoopPhase.Render;
  readonly subPhase = SubPhase.PreUpdate;
  readonly priority = -90;

  constructor(
    private state: XRSessionState,
    private layerManager: XRLayerManager,
    _renderer: EngineRenderer,
  ) {}

  execute(_dt: number, _world: World): void {
    if (!this.state.isPresenting) return;

    const session = this.state.session;
    if (!session) return;

    // Use __xrFrame (set by App.ts animation loop) for consistency with all
    // other XR systems (HitTest, Anchor, Depth, LightProbe, InputPoll).
    const frame = (globalThis as any).__xrFrame as XRFrame | undefined;
    if (frame) {
      this.layerManager.updateLayers(frame);
    }
  }
}
